import React, { useCallback, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import Button from 'design-system/Button';
import Modal from 'utils/packages/Modal';
import Spacer from 'design-system/Spacer';
import Text from 'design-system/Text';
import { IconArrow } from 'design-system/icons/IconArrow';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { rem } from 'design-system/values';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppSelector } from 'store/hooks';
import i18n from 'utils/i18n';
import RowAchAccount from './RowAchAccount';
const AchAccountSelectorModalContent = ({ accounts, payingImmediately, onContinue, }) => {
    const styles = useStyles(styleSet);
    const colors = useColors();
    const currentACHAccount = useAppSelector((store) => store.stripe.customer?.achPaymentMethod);
    const [selectedIndex, setSelectedIndex] = useState(() => {
        if (!currentACHAccount)
            return 0;
        const idx = accounts.findIndex(({ plaidItemId, plaidAccountId }) => plaidItemId === currentACHAccount.plaidItemId &&
            plaidAccountId === currentACHAccount.plaidAccountId);
        return idx === -1 ? 0 : idx;
    });
    const [isFetching, setIsFetching] = useState(false);
    const hasMultipleAccounts = accounts.length > 1;
    const selectedAccount = accounts[selectedIndex];
    const handleOpenPicker = useCallback(() => {
        if (!hasMultipleAccounts)
            return;
        Modal.show(<AchAccountPickerModalContent accounts={accounts} initialIndex={selectedIndex} onSave={(index) => {
                setSelectedIndex(index);
                Modal.hide(undefined, { top: true });
            }}/>, {
            top: true,
            title: 'Select payment account',
            containerStyle: { paddingHorizontal: 0 },
            onBackdropPress: () => Modal.hide(undefined, { top: true }),
            onSwipeComplete: () => Modal.hide(undefined, { top: true }),
            onAndroidBackButtonPress: () => Modal.hide(undefined, { top: true }),
        });
    }, [accounts, hasMultipleAccounts, selectedIndex]);
    const handleContinue = useCallback(async () => {
        if (!selectedAccount)
            return;
        setIsFetching(true);
        onContinue(selectedAccount);
    }, [selectedAccount, onContinue]);
    return (<View style={styles.container}>
      <Spacer h={56}/>
      {selectedAccount ? (<TouchableOpacity onPress={handleOpenPicker} disabled={!hasMultipleAccounts}>
          <View style={[styles.accountRow, styles.selectedAccount]}>
            <View style={{ flex: 1 }}>
              <RowAchAccount account={selectedAccount} style={{ flex: 1 }}/>
            </View>
            <Spacer.W16 />

            {hasMultipleAccounts ? (<IconArrow direction="down" color={colors.elements.secondary}/>) : null}
          </View>
        </TouchableOpacity>) : null}

      <Spacer h={16}/>

      <Text TextThin-16 Secondary centered>
        By tapping continue, I {i18n.t('authorise')} Emma to electronically
        debit my account
        {payingImmediately ? '' : ' after the trial period ends'}
      </Text>

      <Spacer h={16}/>

      <Button brand title="Continue" onPress={handleContinue} isFetching={isFetching} disabled={!selectedAccount} style={styles.button}/>
    </View>);
};
const AchAccountPickerModalContent = ({ accounts, initialIndex, ctaLabel = 'Save', onSave, onContinue, }) => {
    const styles = useStyles(styleSet);
    const [pickerIndex, setPickerIndex] = useState(initialIndex);
    const [isFetching, setIsFetching] = useState(false);
    const handlePress = useCallback(async () => {
        if (onContinue) {
            const account = accounts[pickerIndex];
            if (!account)
                return;
            setIsFetching(true);
            try {
                await onContinue(account);
            }
            finally {
                setIsFetching(false);
            }
            return;
        }
        onSave?.(pickerIndex);
    }, [accounts, onContinue, onSave, pickerIndex]);
    return (<View style={styles.container}>
      <Spacer h={16}/>
      {accounts.map((account, index) => {
            const isSelected = index === pickerIndex;
            return (<TouchableOpacity key={account.plaidAccountId} onPress={() => setPickerIndex(index)}>
            <View style={styles.accountRow}>
              <RowAchAccount account={account} selected={isSelected} style={{ flex: 1 }}/>
            </View>
          </TouchableOpacity>);
        })}

      <Spacer h={16}/>

      <Button brand title={ctaLabel} onPress={handlePress} isFetching={isFetching} style={styles.button}/>
    </View>);
};
export const showAchAccountPickerModal = (props) => {
    Modal.show(<AchAccountPickerModalContent accounts={props.accounts} initialIndex={props.initialIndex} ctaLabel="Continue" onContinue={props.onContinue}/>, {
        title: 'Select payment account',
        containerStyle: { paddingHorizontal: 0 },
        onBackdropPress: props.onDismissed,
        onSwipeComplete: props.onDismissed,
        onAndroidBackButtonPress: props.onDismissed,
    });
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingHorizontal: rem(16),
    },
    pickerTitle: {
        marginTop: rem(8),
    },
    accountRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    selectedAccount: {
        paddingHorizontal: rem(16),
        borderWidth: 1,
        borderRadius: rem(16),
        borderColor: colors.borders.divider,
    },
    accountInfo: {
        flex: 1,
        marginLeft: rem(16),
        marginRight: rem(8),
    },
    button: {
        marginHorizontal: rem(0),
    },
}));
const showAchAccountSelectorModal = (props) => {
    Modal.show(<AchAccountSelectorModalContent {...props}/>, {
        containerStyle: { paddingHorizontal: 0 },
        onBackdropPress: props.onDismissed,
        onSwipeComplete: props.onDismissed,
        onAndroidBackButtonPress: props.onDismissed,
    });
};
export default showAchAccountSelectorModal;
